import { useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { FormikValues } from 'formik';

import getDefaultLayout from '@lyearn/form/src/Form/helper/getDefaultLayout';
import getOrderedFieldNames from '@lyearn/form/src/Form/utils/getOrderedFieldNames';
import { useMultiStep } from '@/components/MultiStep/hooks/useMultiStep';

import { MultiStepFormProps } from '../types';

const OVERRIDES = {
  Section: {
    spacing: 'space-y-40',
  },
};

export default function useMultiStepForm<TFormValues extends FormikValues = FormikValues>({
  sections,
  fields,
  disabledSections,
  navigationRef,
  stepRef,
  onStepChange,
  overrides,
  formName,
  stepStateHandler,
}: MultiStepFormProps<TFormValues>) {
  const firstSectionId = sections[0].id;
  const { inViewStep, setStep } = useMultiStep({
    handle: stepStateHandler,
    formName,
    initialStep: firstSectionId,
  });
  const fieldLayout = getDefaultLayout(fields);
  const orderedFieldNames = useMemo(() => getOrderedFieldNames(fieldLayout), [fieldLayout]);
  const contextValue = useMemo(
    () => ({
      fields,
      focusErrorOnSubmit: false,
      orderedFieldNames,
      overrides: overrides ?? OVERRIDES,
    }),
    [fields, orderedFieldNames, overrides],
  );

  useEffect(() => {
    onStepChange?.(inViewStep);
  }, [inViewStep, onStepChange]);

  const showPrev = !navigationRef && inViewStep !== firstSectionId;
  const showNext = !navigationRef && sections.at(-1)?.id !== inViewStep;

  const onNext = useCallback(() => {
    const inViewSectionIndex = sections.findIndex((section) => section.id === inViewStep);
    const nextEnabledSectionIndex = sections.findIndex(
      (section, i) => !disabledSections?.includes(section.id) && i > inViewSectionIndex,
    );
    nextEnabledSectionIndex !== -1 ? setStep(sections[nextEnabledSectionIndex].id) : undefined;
  }, [disabledSections, inViewStep, sections, setStep]);

  const onPrevious = useCallback(() => {
    const inViewSectionIndex = sections.findIndex((section) => section.id === inViewStep);
    const previousEnabledSectionIndex = sections
      .slice(0, inViewSectionIndex)
      .reverse()
      .findIndex((section) => !disabledSections?.includes(section.id));

    // Because we reversed the array, we need to subtract the index from the original length - 1
    const actualPreviousIndex = inViewSectionIndex - previousEnabledSectionIndex - 1;
    actualPreviousIndex !== -1 ? setStep(sections[actualPreviousIndex].id) : undefined;
  }, [disabledSections, inViewStep, sections, setStep]);

  useImperativeHandle(
    navigationRef,
    () => {
      return {
        onNext,
        onPrevious,
      };
    },
    [onNext, onPrevious],
  );

  useImperativeHandle(
    stepRef,
    () => {
      return {
        step: inViewStep,
        setStep,
      };
    },
    [inViewStep, setStep],
  );

  return {
    contextValue,
    inViewStep,
    setStep,
    onNext: showNext ? onNext : undefined,
    onPrevious: showPrev ? onPrevious : undefined,
  };
}
